import { Button, Card, CardContent, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  ApiRequest_Create_Contact,
  useCreateContact,
  CreateContactDetailDto,
} from '@/api/endpoints/contact/useCreateContact';
import { ContactRole } from '@/api/enums/index';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormAutocomplete, { Option } from '@/components/form/FormAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';

const ContactRoleFallback = {
  TENANT: 'TENANT',
  LANDLORD: 'LANDLORD',
  PROPERTY_MANAGER: 'PROPERTY_MANAGER',
  OWNER_REPRESENTATIVE: 'OWNER_REPRESENTATIVE',
  CLEANING_SERVICE: 'CLEANING_SERVICE',
  HVAC_CONTRACTOR: 'HVAC_CONTRACTOR',
  ELECTRIC_PROVIDER: 'ELECTRIC_PROVIDER',
  WATER_PROVIDER: 'WATER_PROVIDER',
  WASTEWATER_SERVICE: 'WASTEWATER_SERVICE',
  PROPANE_SUPPLIER: 'PROPANE_SUPPLIER',
  TRASH_REMOVAL: 'TRASH_REMOVAL',
  TREE_SERVICE: 'TREE_SERVICE',
  PEST_CONTROL: 'PEST_CONTROL',
  POOL_SERVICE: 'POOL_SERVICE',
  LAWN_CARE: 'LAWN_CARE',
  LANDSCAPING_VENDOR: 'LANDSCAPING_VENDOR',
  SECURITY_PROVIDER: 'SECURITY_PROVIDER',
  SOUND_SYSTEM_VENDOR: 'SOUND_SYSTEM_VENDOR',
  WATER_COOLER_VENDOR: 'WATER_COOLER_VENDOR',
  TAX_AUTHORITY: 'TAX_AUTHORITY',
  INSURANCE_AGENT: 'INSURANCE_AGENT',
  LEGAL_REPRESENTATIVE: 'LEGAL_REPRESENTATIVE',
  OTHER: 'OTHER',
};

const SafeContactRole = ContactRole || ContactRoleFallback;

interface ContactMetadata {
  additional_details?: string;
  [key: string]: unknown;
}

interface ContactFormData
  extends Omit<
    ApiRequest_Create_Contact,
    'details' | 'building_ids' | 'unit_ids' | 'location_ids'
  > {
  details: CreateContactDetailDto[];
  building_ids: number[];
  unit_ids: number[];
  location_ids: Option[];
  additional_details?: string;
}

const contactRoleOptions = [
  { label: 'Tenant', value: SafeContactRole.TENANT },
  { label: 'Landlord', value: SafeContactRole.LANDLORD },
  { label: 'Property Manager', value: SafeContactRole.PROPERTY_MANAGER },
  { label: 'Owner Representative', value: SafeContactRole.OWNER_REPRESENTATIVE },
  { label: 'Cleaning Service', value: SafeContactRole.CLEANING_SERVICE },
  { label: 'HVAC Contractor', value: SafeContactRole.HVAC_CONTRACTOR },
  { label: 'Electric Provider', value: SafeContactRole.ELECTRIC_PROVIDER },
  { label: 'Water Provider', value: SafeContactRole.WATER_PROVIDER },
  { label: 'Wastewater Service', value: SafeContactRole.WASTEWATER_SERVICE },
  { label: 'Propane Supplier', value: SafeContactRole.PROPANE_SUPPLIER },
  { label: 'Trash Removal', value: SafeContactRole.TRASH_REMOVAL },
  { label: 'Tree Service', value: SafeContactRole.TREE_SERVICE },
  { label: 'Pest Control', value: SafeContactRole.PEST_CONTROL },
  { label: 'Pool Service', value: SafeContactRole.POOL_SERVICE },
  { label: 'Lawn Care', value: SafeContactRole.LAWN_CARE },
  { label: 'Landscaping Vendor', value: SafeContactRole.LANDSCAPING_VENDOR },
  { label: 'Security Provider', value: SafeContactRole.SECURITY_PROVIDER },
  { label: 'Sound System Vendor', value: SafeContactRole.SOUND_SYSTEM_VENDOR },
  { label: 'Water Cooler Vendor', value: SafeContactRole.WATER_COOLER_VENDOR },

  { label: 'Tax Authority', value: SafeContactRole.TAX_AUTHORITY },
  { label: 'Insurance Agent', value: SafeContactRole.INSURANCE_AGENT },
  { label: 'Legal Representative', value: SafeContactRole.LEGAL_REPRESENTATIVE },

  { label: 'Other', value: SafeContactRole.OTHER },
];

const contactDetailTypeOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
];

const contactDetailLabelOptions = [
  { label: 'Primary', value: 'primary' },
  { label: 'Work', value: 'work' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Home', value: 'home' },
  { label: 'Other', value: 'other' },
];

const ContactCreatePage: React.FC = () => {
  const { propertyLocations, selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const [formError, setFormError] = useState<Error | null>(null);

  const locationOptions = propertyLocations.map(location => ({
    label: location.name,
    value: location.id,
  }));

  const defaultLocationOptions = selectedPropertyLocation
    ? [{ label: selectedPropertyLocation.name, value: selectedPropertyLocation.id }]
    : [];

  const form = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      role: SafeContactRole.TENANT,
      details: [{ type: 'email', value: '', is_primary: true }],
      building_ids: [],
      unit_ids: [],
      location_ids: defaultLocationOptions,
      additional_details: '',
    },
    mode: 'onChange',
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  const {
    fields: detailFields,
    append: appendDetail,
    remove: removeDetail,
  } = useFieldArray({
    control,
    name: 'details',
  });

  const createContactMutation = useCreateContact({
    onSuccess: () => {
      history.push(PMPRoutes.contactList);
    },
    onError: error => {
      setFormError(error);
    },
  });

  const handleBack = () => {
    history.push(PMPRoutes.contactList);
  };

  const onSubmit = useCallback(
    (data: ContactFormData) => {
      setFormError(null);
      clearErrors('location_ids');

      // Convert Option objects to numbers for API
      const locationIds = data.location_ids.map(option => option.value as number);

      // Validate that at least one location is selected
      if (!locationIds || locationIds.length === 0) {
        setError('location_ids', {
          type: 'required',
          message: 'At least one location is required',
        });
        return;
      }

      // Filter out empty contact details
      const filteredDetails = data.details.filter(detail => detail.value.trim() !== '');

      // Prepare metadata with additional details
      const metadata: ContactMetadata | undefined = data.additional_details?.trim()
        ? { additional_details: data.additional_details.trim() }
        : undefined;

      // Destructure to exclude location_ids from spread
      const { location_ids: _, additional_details: __, ...apiData } = data;

      return createContactMutation.mutateAsync({
        ...apiData,
        metadata,
        details: filteredDetails.length > 0 ? filteredDetails : undefined,
        building_ids: data.building_ids.length > 0 ? data.building_ids : undefined,
        unit_ids: data.unit_ids.length > 0 ? data.unit_ids : undefined,
        location_ids: locationIds,
      });
    },
    [createContactMutation, setError, clearErrors]
  );

  const handleAddDetail = () => {
    appendDetail({ type: 'email', value: '', is_primary: false });
  };

  if (propertyLocations.length === 0) {
    return (
      <MuiPageWrapper>
        <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
          <Typography variant="body2" color="text.secondary">
            No property locations found. Please create a property location first.
          </Typography>
        </Stack>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Stack direction="column" spacing={3} sx={{ flex: '1 1 100%' }}>
        <DashboardHeader title="Add New Contact">
          <Button
            color="secondary"
            variant="outlined"
            size="medium"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
          >
            Back to Contacts
          </Button>
        </DashboardHeader>

        <Typography variant="body3">
          Create a new contact and associate it with one or more property locations.
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={4}>
              {/* Basic Information */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Basic Information
                </Typography>

                <FormTextField
                  label="Contact Name"
                  placeholder="Enter contact name"
                  required
                  control={control}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  {...register('name', {
                    required: 'Contact name is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormSelect
                  label="Role"
                  placeholder="Select contact role"
                  required
                  control={control}
                  options={contactRoleOptions}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  {...register('role', {
                    required: 'Contact role is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormAutocomplete
                  name="location_ids"
                  label="Property Locations"
                  placeholder="Select property locations"
                  required
                  control={control}
                  options={locationOptions}
                  multiple
                  filterSelectedOptions
                  error={!!errors.location_ids}
                  helperText={
                    errors.location_ids?.message ||
                    'Select one or more locations to associate with this contact'
                  }
                  size="small"
                  sx={{ maxWidth: 600 }}
                />

                <FormTextField
                  label="Additional Details"
                  placeholder="Enter any additional information about this contact"
                  control={control}
                  error={!!errors.additional_details}
                  helperText={errors.additional_details?.message}
                  {...register('additional_details')}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ maxWidth: 600 }}
                />
              </Stack>

              {/* Contact Details */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Contact Details
                </Typography>

                {detailFields.map((field, index) => (
                  <Card key={field.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: 2 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ width: '100%' }}
                      >
                        <FormSelect
                          label="Type"
                          control={control}
                          options={contactDetailTypeOptions}
                          {...register(`details.${index}.type`)}
                          sx={{
                            minWidth: { xs: '100%', sm: 120 },
                            maxWidth: { xs: '100%', sm: 150 },
                          }}
                          size="small"
                        />

                        <FormTextField
                          label="Value"
                          placeholder={
                            watch(`details.${index}.type`) === 'email'
                              ? 'Enter email address'
                              : 'Enter phone number'
                          }
                          control={control}
                          error={!!errors.details?.[index]?.value}
                          helperText={errors.details?.[index]?.value?.message}
                          {...register(`details.${index}.value`, {
                            required: 'Value is required',
                            pattern:
                              watch(`details.${index}.type`) === 'email'
                                ? {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: 'Invalid email address',
                                }
                                : undefined,
                          })}
                          sx={{
                            flex: 1,
                            minWidth: { xs: '100%', sm: 200 },
                          }}
                          size="small"
                        />

                        <FormSelect
                          label="Label"
                          control={control}
                          options={contactDetailLabelOptions}
                          {...register(`details.${index}.label`)}
                          sx={{
                            minWidth: { xs: '100%', sm: 120 },
                            maxWidth: { xs: '100%', sm: 150 },
                          }}
                          size="small"
                        />

                        {detailFields.length > 1 && (
                          <IconButton
                            onClick={() => removeDetail(index)}
                            color="error"
                            sx={{
                              mt: { xs: 0, sm: 2 },
                              alignSelf: { xs: 'center', sm: 'flex-start' },
                            }}
                            size="small"
                          >
                            <Trash01Icon />
                          </IconButton>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<PlusIcon />}
                  onClick={handleAddDetail}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    mt: 1,
                  }}
                >
                  Add Contact Detail
                </Button>
              </Stack>

              {/* Error Display */}
              {formError && (
                <Typography color="error" variant="body2">
                  {formError.message}
                </Typography>
              )}

              {/* Submit Button */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
                  Cancel
                </Button>
                <LoadingButton loading={isSubmitting} variant="contained" type="submit">
                  Create Contact
                </LoadingButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </MuiPageWrapper>
  );
};

export default ContactCreatePage;
